// The four numbers at the top of the dashboard.
//
// Every one of them is OUR record of what this workspace did — posts we
// scheduled, deliveries we attempted, accounts you connected here. None of them
// is an audience measurement, because we never asked Facebook, Instagram or
// YouTube for one. That is why the fourth card is "did it get through" rather
// than the engagement figure a dashboard in this category is expected to show:
// a number we cannot honestly produce is not one we will invent.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { nf, rate, when } from "./format";
import type { Dashboard } from "./types";

export interface Stat {
  id: string;
  label: string;
  value: string;
  /** How it compares with the window before, when there is an honest comparison. */
  delta?: { amount: number; better: "up" | "down" } | null;
  hint?: ReactNode;
  tone?: "ok" | "warn" | "err";
  icon: ReactNode;
}

export function StatCard({ stat }: { stat: Stat }) {
  const d = stat.delta;
  const good = d && d.amount !== 0
    ? (d.amount > 0) === (d.better === "up")
    : null;

  return (
    <section className={stat.tone ? `card stat-card tone-${stat.tone}` : "card stat-card"}>
      <span className="stat-card-icon" aria-hidden="true">{stat.icon}</span>
      <div className="stat-card-body">
        <strong className="stat-card-value">{stat.value}</strong>
        <span className="stat-card-label muted">{stat.label}</span>
      </div>
      {d && (
        <span className={`stat-card-delta${good === null ? "" : good ? " up" : " down"}`}>
          {d.amount === 0
            ? "no change"
            : `${d.amount > 0 ? "▲" : "▼"} ${nf.format(Math.abs(d.amount))}`}
          <span className="muted"> vs the period before</span>
        </span>
      )}
      {stat.hint && <span className="stat-card-hint muted">{stat.hint}</span>}
    </section>
  );
}

/** The four, derived from the one dashboard payload. */
export function statsFrom(d: Dashboard, zone: string): Stat[] {
  const t = d.totals;
  const attempted = t.deliveries_published + t.deliveries_failed;

  return [
    {
      id: "scheduled",
      label: "Scheduled posts",
      value: nf.format(t.scheduled_ahead),
      icon: "🗓",
      hint: t.next_scheduled_at
        ? <>next {when(t.next_scheduled_at, zone)}</>
        : <>nothing booked — <Link to="/compose">write one</Link></>,
    },
    {
      id: "published",
      label: `Published in ${d.range.days} days`,
      value: nf.format(t.posts_published),
      icon: "🚀",
      delta: { amount: t.posts_published - t.posts_published_previous, better: "up" },
      hint: <>{nf.format(t.deliveries_published)} deliveries — one per network, per post</>,
    },
    {
      id: "accounts",
      label: "Connected accounts",
      value: nf.format(d.connections.active),
      icon: "🔗",
      tone: d.connections.expiring_soon + d.connections.lapsed > 0 ? "warn" : undefined,
      hint: d.connections.lapsed + d.connections.expiring_soon > 0
        ? <Link to="/connections">
            {d.connections.lapsed + d.connections.expiring_soon} need reconnecting
          </Link>
        : d.connections.active === 0
          ? <Link to="/connections">connect your first account</Link>
          : "all working",
    },
    {
      // Not "engagement". See the note at the top of this file, and the one at
      // the foot of the dashboard.
      id: "delivered",
      label: "Delivered successfully",
      value: attempted === 0 ? "—" : rate(t.deliveries_published, attempted),
      icon: "✅",
      tone: t.deliveries_failed > 0 ? "warn" : attempted > 0 ? "ok" : undefined,
      hint: attempted === 0
        ? "nothing sent in this window yet"
        : t.deliveries_failed > 0
          ? <Link to="/posts">{nf.format(t.deliveries_failed)} failed — see which</Link>
          : `${nf.format(attempted)} attempts, none failed`,
    },
  ];
}
